import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Tooltip } from "@mui/material";
import { INLINE_EDITABLE_SELECTOR } from "../inputs/InlineEditable";

/**
 * Zeigt den vollständigen Text als Tooltip, sobald der Inhalt abgeschnitten ist – und zwar
 * genau einmal.
 *
 * Gemessen wird erst beim Überfahren, nicht beim Rendern: Vorher steht die Spaltenbreite noch
 * nicht fest, und eine Messung je Zelle bei jedem Rendern kostete bei langen Listen einen
 * Reflow pro Zelle.
 *
 * Der Tooltip ist **gesteuert** (`open`), und das ist der Kern: Titel und Öffnen werden in
 * derselben Zustandsänderung gesetzt. Bei einem ungesteuerten Tooltip wechselt der Titel
 * dagegen von leer auf Text, während MUI das Aufklappen schon begonnen hat – MUI hängt dann
 * einen zweiten Popper an, und für die Dauer der Ausblend-Transition stehen beide gleichzeitig
 * auf dem Schirm. Genau das war als doppeltes Infoelement zu sehen.
 *
 * Abgeschnitten ist, was mehr Breite braucht als es hat (`scrollWidth > clientWidth`) – das
 * trifft `noWrap`-Text mit Ellipse. Mehrzeilige Inhalte (Antrag über Baustein) sammeln jede
 * abgeschnittene Zeile einzeln ein; übergeordnete Elemente fallen dabei heraus, damit ihr Text
 * nicht zusätzlich als Ganzes erscheint.
 *
 * Felder zum Ändern in der Zeile (`InlineEditable`) bekommen keinen Tooltip, ebenso wenig, was
 * eins enthält: Der Tooltip wiederholte nur den Wert, und am Feld ist keiner vorgesehen.
 */
const VERZOEGERUNG_MS = 400;

interface AbgeschnittenerInhaltProps {
  children: React.ReactNode;
}

const AbgeschnittenerInhalt: React.FC<AbgeschnittenerInhaltProps> = ({
  children,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [titel, setTitel] = useState("");
  const [offen, setOffen] = useState(false);

  const ermitteln = useCallback((): string => {
    const wurzel = ref.current;
    if (!wurzel) return "";
    const abgeschnitten = [
      wurzel,
      ...Array.from(wurzel.querySelectorAll<HTMLElement>("*")),
      // Ein Pixel Toleranz: Unterpixel-Breiten melden sonst auch dort eine Überlänge,
      // wo nichts abgeschnitten ist.
    ].filter(
      (el) =>
        el.scrollWidth > el.clientWidth + 1 &&
        !el.closest(INLINE_EDITABLE_SELECTOR) &&
        !el.querySelector(INLINE_EDITABLE_SELECTOR),
    );
    return abgeschnitten
      .filter(
        (el) =>
          !abgeschnitten.some((other) => other !== el && el.contains(other)),
      )
      .map((el) => el.textContent?.trim() ?? "")
      .filter(Boolean)
      .join("\n");
  }, []);

  const betreten = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const text = ermitteln();
      setTitel(text);
      setOffen(text !== "");
    }, VERZOEGERUNG_MS);
  }, [ermitteln]);

  const verlassen = useCallback(() => {
    window.clearTimeout(timer.current);
    setOffen(false);
  }, []);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <Tooltip
      title={titel}
      open={offen}
      disableInteractive
      slotProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
    >
      <Box
        ref={ref}
        onMouseEnter={betreten}
        onMouseLeave={verlassen}
        sx={{ minWidth: 0 }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export default AbgeschnittenerInhalt;
